import { call, put, select } from 'redux-saga/effects';

import { updateAppointmentRequested } from 'connect/actions';
import { NPR_ERROR_TYPES } from 'connect/constants';
import { extractErrorCode, extractErrorMessage, sendToLogger } from 'connect/saga/helpers';

import request, { POST } from 'utils/request';

import { SagaHandler } from '../SagaHandler';

export const apiRequestAppointment = async ({
  providerId,
  body,
}) => request(
  POST,
  `${CONFIG.API_URL}/patient/appointment/request/${providerId}`,
  body,
);

const ERROR_TYPES_TO_API_ERROR_MAP = {
  [NPR_ERROR_TYPES.SLOT_TAKEN]:
    'This reserved appointment slot is not available.',
  [NPR_ERROR_TYPES.APPOINTMENT_REQUESTED_ALREADY]:
    'You have already submitted an appointment request with this clinic.',
  [NPR_ERROR_TYPES.NOT_PATIENT]:
    'This patient could not be found.',
  [NPR_ERROR_TYPES.PROVIDER_NO_ONLINE_BOOKING]:
    'This provider does not have online appointment requests enabled.',
  [NPR_ERROR_TYPES.PROVIDER_NO_RESERVED_SLOTS]:
    'This provider does not have reserved slot booking enabled.',
  [NPR_ERROR_TYPES.PHONE_NOT_VERIFIED]:
    'Appointment creation failed: patient phone is not verified.',
  [NPR_ERROR_TYPES.EMAIL_NOT_VERIFIED]:
    'Appointment creation failed: patient email is not verified.',
};

const ERROR_TYPES_TO_API_ERROR_LOOSE_MAP = {
  [NPR_ERROR_TYPES.OCS_WRITE_ERROR]: 'Error encountered while writing appointment on the PMS',
  [NPR_ERROR_TYPES.OCS_WRITE_ERROR]: 'Failed to write appointment',
};

const getErrorType = ({ errorMessage, errorCode }) => {
  // If this error code maps to a known error type, return it
  if (errorCode) {
    const isKnownErrorCode = Object.values(NPR_ERROR_TYPES).some(
      (errorType) => errorType === errorCode,
    );

    if (isKnownErrorCode) {
      return errorCode;
    }
  }

  const errorTypeIndex = Object.values(ERROR_TYPES_TO_API_ERROR_MAP).findIndex(
    (errorMessageToMatch) => errorMessageToMatch.includes(errorMessage),
  );

  const errorTypeLooseIndex = Object.values(ERROR_TYPES_TO_API_ERROR_LOOSE_MAP).findIndex(
    (errorMessageToMatch) => errorMessage.includes(errorMessageToMatch),
  );

  if (errorTypeIndex === -1 && errorTypeLooseIndex === -1) {
    return NPR_ERROR_TYPES.GENERIC;
  }

  if (errorTypeIndex !== -1) {
    return Object.keys(ERROR_TYPES_TO_API_ERROR_MAP)[errorTypeIndex];
  }

  return Object.keys(ERROR_TYPES_TO_API_ERROR_LOOSE_MAP)[errorTypeLooseIndex];
};

const getErrorDetails = (error) => {
  const errorMessage = extractErrorMessage(error);
  const errorCode = extractErrorCode(error);
  const errorType = getErrorType({ errorMessage, errorCode });
  return { errorMessage, errorType };
};

class _RequestAppointment extends SagaHandler {
  constructor() {
    super({
      actionName: 'appointment/REQUEST_APPOINTMENT',
      stateKey: 'requestAppointment',
    });
  }

  * saga({ payload }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;
    const valueHandler = this.value;

    const isLoading = yield select(loadingHandler.selector);
    if (isLoading) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      if (!payload.providerId || !payload.body) {
        throw new Error('Missing providerId or body in requestAppointment payload');
      }

      const appointment = yield call(apiRequestAppointment, payload);

      yield put(valueHandler.update(appointment));
      // Keep legacy action for backwards compatibility
      yield put(updateAppointmentRequested(appointment));
      yield put(successHandler.update(true));
    } catch (error) {
      const { errorMessage, errorType } = getErrorDetails(error);
      yield put(errorHandler.update(errorType));

      const errorDetails = errorType === NPR_ERROR_TYPES.GENERIC
        ? `Unknown error type for requestAppointment error "${errorMessage}"`
        : undefined;
      sendToLogger(error, 'RequestAppointment', errorDetails);
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const RequestAppointment = new _RequestAppointment();
